<template>
	<section class="invoice-report" :aria-label="$t('invoices.reports.heading')">
		<dl class="metric-grid">
			<div
				v-for="metric in reportMetrics"
				:key="metric.key"
				class="metric-card"
				:class="{ 'is-deposit': metric.deposit }"
			>
				<div class="metric-heading">
					<dt>{{ metric.label }}</dt>
					<span class="metric-icon" aria-hidden="true">
						<portal-icon :name="metric.icon" :size="19"></portal-icon>
					</span>
				</div>
				<dd>{{ metric.value }}</dd>
			</div>
		</dl>

		<div class="report-chart-grid">
			<section class="report-chart-card report-chart-card-wide">
				<header class="report-chart-heading">
					<h2>{{ $t('invoices.reports.monthly-invoiced') }}</h2>
				</header>
				<invoice-by-month :invoices="invoices"></invoice-by-month>
			</section>

			<section class="report-chart-card">
				<header class="report-chart-heading">
					<h2>{{ $t('invoices.reports.spend-by-project') }}</h2>
				</header>
				<spend-by-project :invoices="invoices" :projects="projects"></spend-by-project>
			</section>
		</div>
	</section>
</template>

<script>
	import { DateTime } from 'luxon';
	import InvoiceByMonth from '@/modules/invoices/charts/InvoiceByMonth';
	import SpendByProject from '@/modules/invoices/charts/SpendByProject';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'InvoiceCharts',

		props: {
			invoices: { type: Array, required: false, default: () => [] },
			projects: { type: Array, required: false, default: () => [] },
			depositAccount: { type: Object, required: false, default: null },
		},

		components: { PortalIcon, SpendByProject, InvoiceByMonth },

		computed: {
			metrics() {
				const result = {
					totalInvoiced: 0,
					totalReceived: 0,
					avgDaysToPay: 0,
				};
				const validStatusList = ['SENT', 'PARTIAL', 'PAID', 'WRITE-OFF'];
				let totalPayDays = 0;
				let totalPayments = 0;
				const invoices = this.invoices.filter((invoice) => validStatusList.includes(invoice.status));

				invoices.forEach((invoice) => {
					result.totalInvoiced += invoice.total;
					result.totalReceived += invoice.paymentTotal;

					if (invoice.paymentTotal && invoice.payments && invoice.payments.length) {
						const dateSent = DateTime.fromISO(invoice.dateSent);
						const datePaid = DateTime.fromISO(invoice.payments[0].datePaid);
						totalPayDays += Math.round(datePaid.diff(dateSent, 'days').days);
						totalPayments++;
					}
				});

				result.avgDaysToPay = totalPayments ? Math.round(totalPayDays / totalPayments) : '--';
				return result;
			},

			reportMetrics() {
				const currency = this.$store.state.defaultCurrency;
				const money = (value) => this.$formatters.dollars(value, true, true, currency);
				const metrics = [
					{
						key: 'total-invoiced',
						label: this.$t('invoices.reports.total-invoiced'),
						value: money(this.metrics.totalInvoiced),
						icon: 'receipt-text',
					},
					{
						key: 'total-payments',
						label: this.$t('invoices.reports.total-payments'),
						value: money(this.metrics.totalReceived),
						icon: 'credit-card',
					},
					{
						key: 'average-days',
						label: this.$t('invoices.reports.avg-days'),
						value: this.metrics.avgDaysToPay,
						icon: 'timer',
					},
				];

				if (this.depositAccount) {
					metrics.push(
						{
							key: 'deposits-collected',
							label: this.$t('invoices.reports.deposits-collected'),
							value: money(this.depositAccount.liabilities),
							icon: 'download',
							deposit: true,
						},
						{
							key: 'deposits-applied',
							label: this.$t('invoices.reports.deposits-applied'),
							value: money(this.depositAccount.credits),
							icon: 'check',
							deposit: true,
						},
						{
							key: 'balance-remaining',
							label: this.$t('invoices.reports.balance-remaining'),
							value: money(this.depositAccount.balance),
							icon: 'history',
							deposit: true,
						}
					);
				}

				return metrics;
			},
		},
	};
</script>

<style scoped lang="scss">
	.invoice-report {
		container: invoice-report / inline-size;
		display: grid;
		gap: 18px;
		padding: 18px 16px 40px;
	}

	.metric-grid {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 12px;
		margin: 0;
	}

	.metric-card {
		position: relative;
		min-width: 0;
		min-height: 116px;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: 18px;
		padding: 16px;
		overflow: hidden;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 14px);
		background: var(--portal-surface);
		box-shadow: 0 1px 1px rgba(16, 17, 15, 0.02);
		text-align: left;
	}

	.metric-card::before {
		position: absolute;
		inset: 0 auto 0 0;
		width: 3px;
		background: var(--v-primary-base);
		content: '';
		opacity: 0.82;
	}

	.metric-card.is-deposit::before {
		background: var(--v-success-base);
	}

	.metric-heading {
		min-width: 0;
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 10px;
	}

	.metric-card dt {
		min-width: 0;
		flex: 1;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 620;
		letter-spacing: 0.015em;
		line-height: 1.35;
		text-align: left;
	}

	.metric-card dd {
		margin: 0;
		overflow: hidden;
		color: var(--portal-text);
		font-size: clamp(21px, 3.2cqi, 28px);
		font-weight: 620;
		letter-spacing: -0.035em;
		line-height: 1.05;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.metric-icon {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		border-radius: 10px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}

	.metric-card.is-deposit .metric-icon {
		background: var(--v-success_10-base);
		color: var(--v-success-base);
	}

	.report-chart-grid {
		display: grid;
		grid-template-columns: minmax(0, 1fr);
		gap: 16px;
	}

	.report-chart-card {
		min-width: 0;
		overflow: hidden;
		border: 1px solid var(--portal-border);
		border-radius: var(--portal-radius-md);
		background: var(--portal-surface);
		box-shadow: 0 1px 1px rgba(16, 17, 15, 0.02);
	}

	.report-chart-heading {
		display: flex;
		align-items: center;
		min-height: 54px;
		padding: 14px 16px;
		border-bottom: 1px solid var(--portal-border);
	}

	.report-chart-heading h2 {
		margin: 0;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 650;
		letter-spacing: -0.01em;
	}

	@media (max-width: 599px) {
		.invoice-report {
			gap: 14px;
			padding: 14px 12px 28px;
		}

		.metric-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 10px;
		}

		.metric-card {
			min-height: 104px;
			gap: 12px;
			padding: 13px;
		}

		.metric-card dd {
			font-size: clamp(19px, 6vw, 24px);
		}

		.metric-icon {
			width: 30px;
			height: 30px;
			flex-basis: 30px;
		}
	}

	@media (max-width: 359px) {
		.metric-grid {
			grid-template-columns: minmax(0, 1fr);
		}
	}
</style>

<style lang="scss">
	@container invoice-report (min-width: 860px) {
		.invoice-report .report-chart-grid {
			grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
		}
	}

	@container invoice-report (max-width: 620px) {
		.invoice-report .metric-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
</style>
